import React, { memo, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Avatar, Box, IconButton } from '@mui/material';
import { convertToBase64 } from 'src/utils';

import { CloseIcon, PenMenuIcon, UploadAvatarIcon } from '../../Icons';
interface FloatingImageButtonProps {
  children: any;
  onClick: () => void;
  sx?: any;
}

const FloatingImageButton = ({ children, onClick, sx, ...rest }: FloatingImageButtonProps & any) => {
  return (
    <IconButton
      sx={{
        position: 'absolute',
        top: '-16px',
        right: '-16px',
        width: '32px',
        height: '32px',
        backgroundColor: 'white',
        color: '#BF295C',
        fontSize: '22px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.1)',
        ...sx,
      }}
      disableRipple
      onClick={onClick}
      {...rest}
    >
      {children}
    </IconButton>
  );
};

interface AvatarSelectProps {
  onImageChanged: (image: string) => void;
  image: string;
  ref: any;
  sx?: any;
}

const AvatarSelect = memo(
  React.forwardRef(({ onImageChanged, image, sx }: AvatarSelectProps, ref) => {
    const placeholderImageSrc = '/images/select-avatar-placeholder.png';

    const [imageSrc, setImageSrc] = useState<string>(image || placeholderImageSrc);

    const isImageWasAdded = useMemo(() => {
      return imageSrc !== placeholderImageSrc;
    }, [imageSrc]);

    const fileInputRef = useRef<any>(null);
    const formRef = useRef<any>(null);

    const handleUploadImageButton = () => {
      fileInputRef?.current?.click();
    };

    const handleDeleteImage = () => {
      formRef?.current?.reset();
      setImageSrc(placeholderImageSrc);
      onImageChanged('');
    };

    const handleImageChange = async (e: any) => {
      if (e.target.files) {
        const base64 = await convertToBase64(e.target.files[0]);

        setImageSrc(base64);
        onImageChanged(base64);
      }
    };

    useImperativeHandle(ref, () => ({
      deleteImage: handleDeleteImage,
    }));

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          width: 156,
          height: 156,
          ...sx,
        }}
      >
        <Avatar
          src={imageSrc}
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: '12px',
          }}
        />
        <form ref={formRef}>
          <FloatingImageButton
            onClick={handleUploadImageButton}
            sx={{
              fontSize: isImageWasAdded ? '16px' : '22px',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                top: '1px',
                left: '1px',
              }}
            >
              <input
                ref={fileInputRef}
                onChange={handleImageChange}
                style={{
                  display: 'none',
                }}
                type="file"
                accept="image/*"
              />
              {isImageWasAdded ? <PenMenuIcon fontSize="inherit" /> : <UploadAvatarIcon fontSize="inherit" />}
            </Box>
          </FloatingImageButton>
          {isImageWasAdded ? (
            <FloatingImageButton
              onClick={handleDeleteImage}
              sx={{
                top: undefined,
                bottom: '-16px',
                color: '#4F4F4F',
                fontSize: '20px',
                fontWeight: '400',
              }}
            >
              <CloseIcon fontSize="inherit" />
            </FloatingImageButton>
          ) : null}
        </form>
      </Box>
    );
  }),
);

export { AvatarSelect };
